import React from "react";
import { Controls, Gesture, Title, useMediaContext, useMediaState } from '@vidstack/react';
import * as Buttons from './shared/buttons';
import * as Menus from './shared/menus/settings-menus';
import * as Sliders from './shared/sliders';
import { TimeGroup } from './shared/time-group';
import { Logo } from "@/components/player/layouts/custom/Logo";
import { LanguageSwitcher } from "@/components/player/language/LanguageSwitcher";
import { PlayerSize, usePlayerLayoutContext } from "@/components/player/layouts/PlayerLayoutContext";
import { LargePlayButton, PlayerCenterPlayButtonProps } from "@/components/player/layouts/custom/PlayerCenterPlayButton";
import { BufferingIndicator } from "@/components/player/layouts/shared/buffering-indicator";
import { BraivCaptions } from "@/components/player/layouts/shared/captions";
import { LanguageMenuLocation, PlayButtonShape } from "@/lib/types";
import { CaptionButton } from "@/components/player/language/CaptionButton";
import { useSignal } from "maverick.js/react";
export interface VideoLayoutProps {
  thumbnails?: string;
  logoUrl?: string;
  logoVisible?: boolean;
  titleVisible?: boolean;
  showCenterPlayButton?: boolean;
  centrePlayButtonShape?: PlayButtonShape;
  displayFlags?: boolean;
  languageMenuLocation?: LanguageMenuLocation;
}
export function VideoLayout(props: VideoLayoutProps) {
  const streamType = useMediaState('streamType');
  const media = useMediaContext();

  // @ts-ignore
  const $load = useSignal(media.$props.load),
    $canLoad = useMediaState('canLoad'),
    isLoadLayout = $load === 'play' && !$canLoad;
  const {
    playerSize
  } = usePlayerLayoutContext();
  console.log('Player size:', playerSize, 'streamType:', streamType);
  if (isLoadLayout) {
    return <VideoLoadLayout centrePlayButtonShape={props.centrePlayButtonShape} playerSize={playerSize} />;
  } else if (streamType === 'unknown') {
    return <BufferingIndicator />;
  } else if (playerSize === 'small' || playerSize === 'extra-small') {
    return <VideoSmallLayout {...props} />;
  } else {
    return <VideoLargeLayout {...props} />;
  }
}
export function VideoLargeLayout({
  thumbnails,
  logoUrl,
  logoVisible,
  titleVisible,
  showCenterPlayButton,
  centrePlayButtonShape,
  displayFlags,
  languageMenuLocation
}: VideoLayoutProps) {
  console.log('VideoLargeLayout');
  return <div className="vds-video-layout" data-match="true" data-lg data-sentry-component="VideoLargeLayout" data-sentry-source-file="video-layout.tsx">
            <Gestures data-sentry-element="Gestures" data-sentry-source-file="video-layout.tsx" />
            <BufferingIndicator data-sentry-element="BufferingIndicator" data-sentry-source-file="video-layout.tsx" />
            <BraivCaptions data-sentry-element="BraivCaptions" data-sentry-source-file="video-layout.tsx" />
            {titleVisible && <div className="vds-video-title-container">
                    <div className="vds-video-title-wrapper">
                        <Title className="vds-video-title" />
                    </div>
                </div>}
            <Controls.Root className={`vds-controls`} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx">
                <Controls.Group className={`vds-controls-group vds-controls-top-group`} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx">
                    <Logo url={logoUrl} visible={logoVisible} size={'medium'} data-sentry-element="Logo" data-sentry-source-file="video-layout.tsx" />
                    <ControlsSpacer data-sentry-element="ControlsSpacer" data-sentry-source-file="video-layout.tsx" />
                    {!languageMenuLocation || languageMenuLocation == "top" ? <LanguageSwitcher size={'large'} placement={"bottom center"} tooltipPlacement={"bottom"} displayFlags={!!displayFlags} showAsDropdown={!displayFlags} /> : null}
                    <ControlsSpacer data-sentry-element="ControlsSpacer" data-sentry-source-file="video-layout.tsx" />
                    <Menus.SettingsMenu placement="bottom end" tooltipPlacement="bottom end" languageMenuSettings={{
          languageMenuLocation,
          displayFlags
        }} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                </Controls.Group>
                <div className="vds-controls-spacer" />
                <Controls.Group className={`vds-controls-group vds-controls-center-group`} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx">
                    <ControlsSpacer data-sentry-element="ControlsSpacer" data-sentry-source-file="video-layout.tsx" />
                    {showCenterPlayButton ? <LargePlayButton size="medium" shape={centrePlayButtonShape} /> : null}
                    <ControlsSpacer data-sentry-element="ControlsSpacer" data-sentry-source-file="video-layout.tsx" />
                </Controls.Group>
                <div className="vds-controls-spacer" />
                <div className="vds-controls-bottom-group">
                    <Controls.Group className={`vds-controls-group`} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx">
                        <Buttons.Play tooltipPlacement="top start" data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                        <Sliders.VolumePopup tooltip={'top'} orientation={'horizontal'} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                        <div className="control-bar-divider" />
                        <TimeGroup data-sentry-element="TimeGroup" data-sentry-source-file="video-layout.tsx" />
                        <Sliders.Time thumbnails={thumbnails} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                        <div className="control-bar-divider" />
                        <CaptionButton tooltipPlacement="top" data-sentry-element="CaptionButton" data-sentry-source-file="video-layout.tsx" />
                        <Buttons.AirPlay tooltipPlacement="top" data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                        <Buttons.GoogleCast tooltipPlacement="top" data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                        <Buttons.PIP tooltipPlacement="top" data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                        <Buttons.Fullscreen tooltipPlacement="top end" data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                    </Controls.Group>
                </div>
            </Controls.Root>
        </div>;
}
export function VideoSmallLayout({
  thumbnails,
  logoUrl,
  logoVisible,
  centrePlayButtonShape,
  displayFlags,
  languageMenuLocation
}: VideoLayoutProps) {
  console.log('VideoSmallLayout');
  return <div className="vds-video-layout" data-match="true" data-sm data-size="sm" data-sentry-component="VideoSmallLayout" data-sentry-source-file="video-layout.tsx">
            <Gestures data-sentry-element="Gestures" data-sentry-source-file="video-layout.tsx" />
            <BufferingIndicator data-sentry-element="BufferingIndicator" data-sentry-source-file="video-layout.tsx" />
            <BraivCaptions data-sentry-element="BraivCaptions" data-sentry-source-file="video-layout.tsx" />
            <Controls.Root className={`vds-controls`} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx">
                <Controls.Group className={`vds-controls-group vds-controls-top-group-small`} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx">
                    <Logo url={logoUrl} visible={logoVisible} size={'small'} data-sentry-element="Logo" data-sentry-source-file="video-layout.tsx" />
                    <ControlsSpacer data-sentry-element="ControlsSpacer" data-sentry-source-file="video-layout.tsx" />
                          {!languageMenuLocation || languageMenuLocation == "top" ? <LanguageSwitcher size={'small'} placement={"bottom center"} tooltipPlacement={"bottom"} displayFlags={!!displayFlags} showAsDropdown={true} portalClass={"vds-video-layout"} /> : null}
                    <ControlsSpacer data-sentry-element="ControlsSpacer" data-sentry-source-file="video-layout.tsx" />
                    <Menus.SettingsMenu placement='bottom' tooltipPlacement="bottom" portalClass={"vds-video-layout"} languageMenuSettings={{
          languageMenuLocation,
          displayFlags
        }} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                    <Sliders.VolumePopup tooltip={'bottom'} orientation={'vertical'} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                </Controls.Group>
                <div className="vds-controls-spacer" />
                <Controls.Group className={`vds-controls-group vds-controls-center-group`} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx">
                    <ControlsSpacer data-sentry-element="ControlsSpacer" data-sentry-source-file="video-layout.tsx" />
                    <LargePlayButton size="small" shape={centrePlayButtonShape} data-sentry-element="LargePlayButton" data-sentry-source-file="video-layout.tsx" />
                    <ControlsSpacer data-sentry-element="ControlsSpacer" data-sentry-source-file="video-layout.tsx" />
                </Controls.Group>
                <div className="vds-controls-spacer" />
                <div className="vds-controls-bottom-group-small">
                    <Controls.Group className={`vds-controls-group vds-controls-time-group-small`} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx">
                        <Sliders.Time thumbnails={thumbnails} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                    </Controls.Group>
                    <Controls.Group className={`vds-controls-group vds-controls-main-group-small`} data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx">
                        <TimeGroup data-sentry-element="TimeGroup" data-sentry-source-file="video-layout.tsx" />
                        <div className="vds-controls-spacer" />
                        <CaptionButton tooltipPlacement="top" data-sentry-element="CaptionButton" data-sentry-source-file="video-layout.tsx" />
                        <Buttons.AirPlay tooltipPlacement="top" data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                        <Buttons.GoogleCast tooltipPlacement="top" data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                        <Buttons.PIP tooltipPlacement="top" data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                        <Buttons.Fullscreen tooltipPlacement="top end" data-sentry-element="unknown" data-sentry-source-file="video-layout.tsx" />
                    </Controls.Group>
                </div>
            </Controls.Root>
        </div>;
}
export function Gestures() {
  return <div className="vds-gestures" data-sentry-component="Gestures" data-sentry-source-file="video-layout.tsx">
            <Gesture className="vds-gesture" event="pointerup" action="toggle:paused" data-sentry-element="Gesture" data-sentry-source-file="video-layout.tsx" />
            <Gesture className="vds-gesture" event="dblpointerup" action="toggle:fullscreen" data-sentry-element="Gesture" data-sentry-source-file="video-layout.tsx" />
            <Gesture className="vds-gesture" event="pointerup" action="toggle:controls" data-sentry-element="Gesture" data-sentry-source-file="video-layout.tsx" />
            <Gesture className="vds-gesture" event="dblpointerup" action="seek:-10" data-sentry-element="Gesture" data-sentry-source-file="video-layout.tsx" />
            <Gesture className="vds-gesture" event="dblpointerup" action="seek:10" data-sentry-element="Gesture" data-sentry-source-file="video-layout.tsx" />
        </div>;
}
export function ControlsSpacer() {
  return <div className="vds-controls-spacer" data-sentry-component="ControlsSpacer" data-sentry-source-file="video-layout.tsx" />;
}
function VideoLoadLayout({
  centrePlayButtonShape = "circle",
  playerSize = "medium"
}: {
  centrePlayButtonShape?: PlayButtonShape;
  playerSize?: PlayerSize;
}) {
  return <div className="vds-load-container" data-sentry-component="VideoLoadLayout" data-sentry-source-file="video-layout.tsx">
            <BufferingIndicator data-sentry-element="BufferingIndicator" data-sentry-source-file="video-layout.tsx" />
            <LargePlayButton size={playerSize === "small" ? "small" : "medium"} shape={centrePlayButtonShape} data-sentry-element="LargePlayButton" data-sentry-source-file="video-layout.tsx" />
        </div>;
}
function VideoErrorLayout() {
  return <div className="vds-error-container" data-sentry-component="VideoErrorLayout" data-sentry-source-file="video-layout.tsx">
            <BufferingIndicator data-sentry-element="BufferingIndicator" data-sentry-source-file="video-layout.tsx" />
        </div>;
}